import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import logo from "../../images/logo/workway_1000.png";
import { useAuth } from "../../components/AuthContext";
import {
  FaCog,
  FaUserCircle,
  FaChevronDown,
  FaSignOutAlt,
  FaQuestionCircle,
  FaCreditCard,
  FaUserEdit,
} from "react-icons/fa";

const Navbar = () => {
  const { user, loginWithGoogle, logout, deleteAccount, isAdmin, isCustomer } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // 외부 클릭 핸들러
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 메뉴 토글 핸들러
  const closeMenu = () => setIsMobileMenuOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("정말로 회원을 탈퇴하시겠습니까? 모든 정보가 영구적으로 삭제됩니다.")) {
      const result = await deleteAccount();
      if (result.success) {
        alert("탈퇴가 완료되었습니다.");
        navigate("/");
      } else {
        alert(result.error);
      }
    }
  };

  // 메뉴 아이템 정의

  const getMenuItems = () => {
    const items = [
      { to: "/", label: "홈" },
      {
        label: "GWS 소개",
        to: "/serviceintro",
        submenu: [
          { to: "/serviceintro/overview", label: "서비스 개요" },
          { to: "/serviceintro/education", label: "GWS 활용 시나리오" },
          { to: "/serviceintro/features", label: "주요 기능" },
          { to: "/serviceintro/benefits", label: "도입 혜택" },
        ],
      },
      {
        label: "제품 안내",
        to: "/productinfo/business",
        submenu: [
          { to: "/productinfo/business", label: "Business" },
          { to: "/productinfo/enterprise", label: "Enterprise" },
          { to: "/productinfo/pricing", label: "가격 정책" },
        ],
      },   
      {
        label: "Workway 도입 혜택",
        to: "/productinfo/workappsheet",
        submenu: [
            
          { to: "/serviceintro/workappsheet", label: "회사관리 시스템" },
        ],
      },
      {
        label: "커뮤니티",
        to: "/board/notice",
        submenu: [
          { to: "/board/notice", label: "공지사항" },
          { to: "/board/update", label: "구글 업데이트" },
          { to: "/board/faq", label: "자주 묻는 질문" },
        ],
      },
      {
        href: "https://blog.naver.com/work-way",
        label: "GWS 소식(블로그)",
        external: true,
      },
      // 로그인 상태에 따라 다른 메뉴 추가
      ...(user
        ? [
            // 로그인한 경우 지원게시판 메뉴 추가
            {
              to: "/support",
              label: "지원게시판",              
              highlight: true, // 강조 표시를 위한 속성 추가
            },
            {
              to: "/payment",
              label: "결제 관리",
              highlight: true,
              highlightType: "payment", // 결제 전용 강조 타입 추가
              icon: <FaCreditCard className="w-4 h-4" />,
            },
          ]
        : [
            // 로그인하지 않은 경우 무료 체험 메뉴 추가
            {
              href: "http://pf.kakao.com/_jyebn/chat",
              label: "무료 체험",
              external: true,
            },
          ]),
    ];

    return items;
  };
  // 네비게이션 링크 렌더링
  const renderNavLinks = () => (
    <>
      {getMenuItems().map((item, index) => (
        <NavItem key={index} {...item} onClick={closeMenu} />
      ))}
    </>
  );

  // 로고 섹션
  const LogoSection = () => (
    <div className="navbar-logo">
      <Link to="/" aria-label="Go to homepage">
        <img src={logo} alt="WORKWAY Logo" className="h-8 sm:h-10" />
      </Link>
    </div>
  );

  // 모바일 버튼 그룹
  const MobileButtons = () => (
    <div className="mobile-buttons-container flex items-center h-full gap-2">
      <button
        className="mobile-user-btn flex items-center gap-2"
        onClick={() => user ? handleLogout() : navigate('/login')}
      >
        {user ? (
          <>
            <span className="text-sm font-medium truncate max-w-[100px] whitespace-nowrap">
              {user.name || user.displayName || "사용자"}님
            </span>
            <FaUserCircle className="w-6 h-6 text-blue-500 flex-shrink-0" />
          </>
        ) : (
          <FaUserCircle className="w-6 h-6 text-gray-400" />
        )}
      </button>
      <button className="hamburger-icon" onClick={toggleMobileMenu}>
        ☰
      </button>
    </div>
  );

  // DesktopAuthSection 컴포넌트
  const DesktopAuthSection = () => (
    <div className="auth-section">
      {user ? (
        <UserMenu
          user={user}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onDeleteAccount={handleDeleteAccount}
          ref={userMenuRef}
        />
      ) : (
        <button
          onClick={() => navigate('/login')}
          className="flex items-center px-6 py-2.5 border-2 border-blue-500 rounded-lg text-blue-600 hover:bg-blue-100 hover:text-white transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
        >
          <FaUserCircle className="w-5 h-5 mr-2 text-blue-500 group-hover:text-white" />
          <span>로그인</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="navbar-container">
      <nav className="navbar">
        <LogoSection />
        <MobileButtons />
        <ul className={`nav-links ${isMobileMenuOpen ? "mobile-open" : ""}`}>
          {renderNavLinks()}
        </ul>
        <DesktopAuthSection />
      </nav>
    </div>
  );
};

// NavItem 컴포넌트
const NavItem = ({
  to,
  href,
  label,
  submenu,
  external,
  onClick,
  icon,
  highlight,
  highlightType, // 추가: payment 등
}) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = window.innerWidth <= 768;

  // 현재 경로가 서브메뉴 항목과 일치하는지 확인
  const isCurrentPath =
    to &&
    (location.pathname === to ||
      (submenu && submenu.some((item) => location.pathname === item.to)));

  // 서브메뉴가 활성화되어야 하는지 확인
  const shouldShowSubmenu =
    submenu &&
    (location.pathname.startsWith(to) ||
      submenu.some((item) => location.pathname === item.to));

  // 경로 변경시 서브메뉴 상태 업데이트
  useEffect(() => {
    if (shouldShowSubmenu && !isMobile) {
      setIsSubMenuOpen(true);
    } else {
      setIsSubMenuOpen(false);
    }
  }, [location.pathname, shouldShowSubmenu, isMobile]);

  const getHighlightClass = () => {
    if (!highlight) return `nav-link text-base font-semibold hover:text-blue-600 transition-colors ${
      isCurrentPath ? "active text-blue-600" : "text-gray-700"
    }`;

    if (highlightType === "payment") {
      return `nav-link text-base font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-md transition-all transform hover:scale-105 flex items-center gap-2 border-none cursor-pointer`;
    }

    return `nav-link text-base font-bold px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-md transition-all transform hover:scale-105 flex items-center gap-2 border-none cursor-pointer`;
  };

  const linkClass = getHighlightClass();

  const handleClick = (e) => {
    if (submenu) {
      e.preventDefault();
      setIsSubMenuOpen(!isSubMenuOpen);
    } else {
      onClick?.();
    }
  };

  const handleSubMenuClick = () => {
    onClick?.();
    if (!isMobile) {
      setIsSubMenuOpen(false);
    }
  };

  return (
    <li
      className="relative nav-item"
      onMouseEnter={() => !isMobile && submenu && setIsSubMenuOpen(true)}
      onMouseLeave={() => !isMobile && submenu && setIsSubMenuOpen(false)}
    >
      {external ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkClass} flex items-center justify-between w-full`}
        >
          <span>{label}</span>
        </a>
      ) : (
        <Link
          to={to}
          className={`${linkClass} flex items-center justify-between w-full p-2`}
          onClick={handleClick}
        >
          {icon && <span className="mr-2">{icon}</span>}
          <span>{label}</span>
          {submenu && (
            <FaChevronDown
              className={`w-3 h-3 transition-transform duration-300 ${
                isSubMenuOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </Link>
      )}
      {submenu && (
        <ul
          className={`
          submenu-container
          bg-white transition-all duration-300 ease-in-out
          ${isSubMenuOpen ? "block" : "hidden"}
          ${
            isMobile
              ? "relative w-full pl-4 py-2 space-y-2"
              : "absolute left-0 top-full w-48 shadow-lg rounded-md py-2 z-50"
          }
        `}
        >
          {submenu.map((item, index) => (
            <li key={index}>
              <Link
                to={item.to}
                className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                  location.pathname === item.to
                    ? "text-blue-600 bg-blue-50"
                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                } ${item.icon ? "flex items-center" : ""}`}
                onClick={handleSubMenuClick}
              >
                {item.icon && <span className="mr-2">{item.icon}</span>}
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

// UserMenu 컴포넌트
const UserMenu = React.forwardRef(({ user, isAdmin, onLogout, onDeleteAccount }, ref) => (
  <div className="user-info flex items-center gap-3" ref={ref}>
    <div className="flex items-center gap-2">
      <FaUserCircle className="w-6 h-6 text-blue-500 flex-shrink-0" />
      <span className="text-base font-semibold whitespace-nowrap">
        {user.name || user.displayName || "사용자"}님
      </span>
    </div>
    {isAdmin && (
      <Link
        to="/workadmin"
        className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
        title="관리자 대시보드"
      >
        <FaCog className="w-5 h-5" />
      </Link>
    )}
    <Link
      to="/profile"
      className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
      title="내 정보 관리"
    >
      <FaUserEdit className="w-5 h-5" />
    </Link>
    <button
      onClick={onLogout}
      className="flex items-center text-gray-700 hover:text-red-600 transition-colors"
      title="로그아웃"
    >
      <FaSignOutAlt className="w-5 h-5" />
    </button>
  </div>
));

export default Navbar;
